import {FCWithHTMLAttributes} from "@/app/types/reactTypes";
import {SVGProps} from "react";
import clsMerge from "@/app/util/clsMerge";

interface DotProps {
	left: number;
	top: number;
	radius: number;
	className?: string;
	opacity?: number;
}

const Dot: React.FC<DotProps> = ({
	left,
	top,
	radius,
	className = "",
	opacity = 1,
}) => {
	return (
		<circle
			cx={left}
			cy={top}
			r={radius}
			opacity={opacity}
			className={clsMerge("fill-white", className, "absolute")}
		/>
	);
};

const locations: [number, number][] = [
	[2, 2],
	[2, 33],
	[2, 65],
	[29, 2],
	[29, 33],
	[29, 106],
	[55, 2],
	[55, 33],
	[55, 65],
	[82, 2],
	[82, 33],
	[82, 65],
	[108, 2],
	[108, 33],
	[108, 65],
	[108, 146],
	[135, 2],
	[135, 33],
	[135, 65],
	[135, 106],
	[158, 2],
	[158, 33],
	[158, 65],
	[158, 106],
	[158, 146],
	[184, 2],
	[184, 33],
	[184, 65],
	[184, 146],
	[211, 2],
	[211, 33],
	[211, 106],
	[211, 146],
	[237, 2],
	[237, 33],
	[237, 65],
	[263, 2],
	[263, 33],
	[263, 65],
	[263, 106],
	[290, 2],
	[290, 33],
	[290, 65],
	[290, 106],
	[290, 146],
	[314, 2],
	[314, 33],
	[314, 65],
	[340, 2],
	[340, 33],
	[340, 65],
	[340, 106],
	[340, 146],
];

interface DotsGridProps extends SVGProps<SVGSVGElement> {
	dotClassName?: string;
	locationsFilter?: (location: [number, number]) => boolean;
	spreadX?: number;
	spreadY?: number;
	dotRadius?: number;
	dotOpacity?: number;
}

/**
 * This should not be a shared component because importing SVGs is best
 * practice. `<DotsGrid>` just can't import static SVGs because it's
 * constructed out of code.
 */
const Svg: React.FC<SVGProps<SVGSVGElement>> = (props) => (
	<svg xmlns="http://www.w3.org/2000/svg" fill="none" {...props} />
);

/**
 * Component to render a decorative grid of dots.
 *
 * Non-default props:
 *  - dotClassName?: The className to apply to each dot.
 *  - locationsFilter?: A function that takes a location and returns
 * 			whether to include it in the grid. This is a slightly janky solution
 * 			to the problem of how to hide dots that would overlap other elements.
 *  - spreadX/Y?: Horizontal and vertical scaling factor for the grid, if you
 * 			want to make the grid wider or taller without making the dots bigger.
 */
const DotsGrid: FCWithHTMLAttributes<DotsGridProps> = ({
	className = "",
	dotClassName = "",
	dotRadius = 2,
	dotOpacity = 1,
	spreadX = 1,
	spreadY = 1,
	locationsFilter = () => true,
	...props
}) => {
	const width = 343 * spreadX;
	const height = 187 * spreadY;
	return (
		<Svg
			width={width}
			height={height}
			viewBox={`0 0 ${width} ${height}`}
			className={className}
			{...props}
		>
			{locations.filter(locationsFilter).map(([x, y], i) => (
				<Dot
					key={i}
					left={x * spreadX}
					top={y * spreadY}
					radius={dotRadius}
					className={dotClassName}
					opacity={dotOpacity}
				/>
			))}
		</Svg>
	);
};

export default DotsGrid;

const locations2: [number, number][] = [
	[3, 3],
	[3, 118],
	[48, 3],
	[48, 118],
	[102, 3],
	[102, 57],
	[156, 3],
	[156, 118],
	[210, 3],
	[210, 57],
	[264, 3],
	[264, 57],
	[264, 118],
	[318, 3],
	[318, 118],
	[370, 3],
	[370, 57],
	[424, 3],
	[424, 57],
	[424, 118],
];

/**
 * See above.
 */
export const DotsGrid2: FCWithHTMLAttributes<DotsGridProps> = ({
	className = "",
	dotClassName = "",
	locationsFilter = () => true,
	...props
}) => {
	return (
		<Svg
			width="427"
			height="121"
			viewBox="0 0 427 121"
			className={className}
			{...props}
		>
			{locations2.filter(locationsFilter).map(([x, y], i) => (
				<Dot key={i} left={x} top={y} radius={3} className={dotClassName} />
			))}
		</Svg>
	);
};
